// modules
import { useState, useEffect, useMemo } from "react";
import { sortBy } from "lodash";

// constants
import { AccountManagement as Constants } from "constants/global";
import SearchFilterIcon from "components/atoms/SearchFilterIcon/SearchFilterIcon";

// Redux
import { updateUserSearchKeyPressed, toggleFilter, showHideFilter, updateCurrentGroup, isUserSearchKeyPressed } from "redux/userList/userSlice";

// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// styles
import styles from "containers/RoleManagement/roleManagementComponent.module.css";

// Types
import type { FilterItem } from "containers/RoleManagement/useRoleManagement";

export type GroupDetails = {
	active: string;
	group_code: string;
	group_desc: string;
	group_id: any;
	permissions?: Array<string>;
};

export type AccountManagementPageProps = {
	getGroupHeader: (_voucherTypeHandler?: () => void, _voucherDownloadHandler?: () => void, groupData?: any) => void;
	generateCols: () => void;
	handleSearchTenantCode: (input: any) => void;
	handleKeyDown: (input: any) => void;
	handleUserSearch: (input: any) => void;
	updateEnterKeyEvent: () => void;
	groupList?: Array<GroupDetails>;
	searchTenantCode: string;
	searchUser: string;
	defaultGroupKey: string;
};

export function useAccountManagementPage(): AccountManagementPageProps {
	const dispatch = useAppDispatch();

	const showFilter = useAppSelector(showHideFilter);
	const enterKeyPressed = useAppSelector(isUserSearchKeyPressed);

	const [searchTenantCode, setSearchTenantCode] = useState("");
	const [searchUser, setSearchUser] = useState("");
	const filterData: FilterItem = {};

	useEffect(() => {
		dispatch(toggleFilter({}));
	}, []);

	const groupList = useMemo(() => {
		dispatch(updateCurrentGroup(Constants.TENANT.group_code));
		return [Constants.TENANT];
	}, []);

	const defaultGroupKey = groupList?.[0]?.group_code;

	const handleSearchTenantCode = (event: any) => {
		event.preventDefault();
		setSearchUser("");
		setSearchTenantCode(event.target.value);
	};

	const handleKeyDown = (event: any) => {
		if (event.key === "Enter") {
			dispatch(updateUserSearchKeyPressed(!enterKeyPressed));
		}
	};

	const handleUserSearch = (event: any) => {
		event.preventDefault();
		setSearchTenantCode("");
		setSearchUser(event.target.value);
	};

	const updateEnterKeyEvent = () => {
		dispatch(updateUserSearchKeyPressed(!enterKeyPressed));
	};

	const handleToggleFilter = (filterValues: FilterItem, groupCode: string) => {
		filterValues[groupCode] = !showFilter[groupCode];
		dispatch(updateCurrentGroup(groupCode));
		dispatch(toggleFilter(filterValues));
	};

	const getGroupHeader = (_voucherTypeHandler?: () => void, _voucherDownloadHandler?: () => void, groupData?: Array<any>) => {
		return sortBy(groupData, ["group_code"]).map((item: any) => {
			const key = item?.group_code;

			filterData[item.group_code] = showFilter && showFilter[item.group_code] ? !showFilter[item.group_code] : false;

			return {
				cell1: <p className={styles.groupTitle}>{item.group_desc}</p>,

				cell2: (
					<SearchFilterIcon
						label={Constants.FILTER_LABEL}
						defaultSelectedFilterValues={[Constants.DEFAULT_STATUS[0].value, Constants.DEFAULT_STATUS[1].value]}
						defaultSelectedFilter={Constants.DEFAULT_STATUS}
						groupCode={item.group_code}
						onClick={() => handleToggleFilter(filterData, item.group_code)}
					/>
				),
				key: key,
				id: key
			};
		});
	};

	const generateCols = () => {
		return [
			{
				dataIndex: "cell1",
				title: <p></p>,
				width: "90%"
			},
			{
				dataIndex: "cell2",
				title: <p></p>
			}
		];
	};

	return {
		groupList,
		searchTenantCode,
		searchUser,
		defaultGroupKey,
		getGroupHeader,
		generateCols,
		handleSearchTenantCode,
		handleKeyDown,
		handleUserSearch,
		updateEnterKeyEvent
	};
}
